import React, {useEffect, useMemo, useState} from 'react';
import {PlusOutlined} from '@ant-design/icons';
import {Input, Select, Button, Modal, Col, Form, Row} from 'antd';
import {selectorItemsApi} from '../apiCalls/selectorItemsApi';
import {SelectorItem, SelectorItems} from '../apiCalls/selectorItems';

type SelectValueLike = {name: string; id: string} | string | undefined;

const selectorItemsCache = new Map<string, SelectorItems>();

const getOptionValue = (item: SelectorItem): string => (item.id ? item.id : item.name);

const resolveSelectValue = (rawValue: SelectValueLike, options: SelectorItems): string | undefined => {
  if (rawValue == null || rawValue === '') {
    return undefined;
  }

  if (typeof rawValue === 'string') {
    const match = options.find((item) => item.id === rawValue || item.name === rawValue);
    return match ? getOptionValue(match) : rawValue;
  }

  if (rawValue.id) {
    return rawValue.id;
  }

  if (rawValue.name) {
    const match = options.find((item) => item.name === rawValue.name);
    return match ? getOptionValue(match) : rawValue.name;
  }

  return undefined;
};

const SelectComponent: React.FC<{
  onChange?: (value: string) => void;
  value?: string;
  title: string;
  defaultValue?: {name: string; id: string} | string;
  endpoint?: string;
  postEndpoint?: string;
  postExtraValues?: any;
  canDoPostRequest?: boolean;
  disabled?: boolean;
  searchable?: boolean;
}> = ({
  onChange,
  value,
  endpoint,
  postEndpoint,
  postExtraValues = {},
  title,
  defaultValue,
  canDoPostRequest = true,
  disabled = false,
  searchable = false,
}) => {
  const [items, setItems] = useState<SelectorItems>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [internalValue, setInternalValue] = useState<string | undefined>(undefined);

  const [form] = Form.useForm();

  useEffect(() => {
    fetchItems();
  }, []);

  const resolvedDefaultValue = useMemo(
    () => resolveSelectValue(defaultValue, items),
    [defaultValue, items],
  );

  useEffect(() => {
    if (value !== undefined) {
      return;
    }

    setInternalValue((currentValue) => currentValue ?? resolvedDefaultValue);
  }, [resolvedDefaultValue, value]);

  const fetchItems = async (forceRefresh = false) => {
    if (!endpoint) return;

    const cachedItems = selectorItemsCache.get(endpoint);
    if (cachedItems && !forceRefresh) {
      setItems(cachedItems);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const data = await selectorItemsApi.getItemList(endpoint);

      setItems(data);
      selectorItemsCache.set(endpoint, data);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (canDoPostRequest) {
        const usedPostEndpoint = postEndpoint || endpoint;

        if (!usedPostEndpoint) {
          throw new Error('There is no valid endpoint to POST request.');
        }

        setConfirmLoading(true);
        await selectorItemsApi.setSelectItemList(usedPostEndpoint, {...postExtraValues, name: values.name});
        onChange && onChange(values.name);
        setIsModalVisible(false);
        fetchItems(true);
      }
    } catch (errors) {
      // error handled silently
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleChange = (selectedValue: string) => {
    if (value === undefined) {
      setInternalValue(selectedValue);
    }
    onChange && onChange(selectedValue);
  };

  const selectedValue = value ?? internalValue;

  return (
    <div>
      <Modal
        confirmLoading={confirmLoading}
        title={`Add new ${title}`}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item label="Name" name="name" rules={[{required: true, message: 'Please input name'}]}>
                <Input placeholder="Enter name" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Select
        placeholder="Elegir opción"
        onChange={handleChange}
        disabled={disabled}
        value={selectedValue}
        loading={isLoading}
        showSearch={searchable}
        optionFilterProp="label"
        dropdownRender={(menu) => {
          if (disabled || !canDoPostRequest) {
            return <div key={`option-${menu}`}>{menu}</div>;
          }

          return (
            <div key={`option-${menu}`}>
              {menu}
              <Button type="text" icon={<PlusOutlined />} onClick={addItem}>
                Add item
              </Button>
            </div>
          );
        }}
        options={items.map((item: SelectorItem) => ({label: item.name, value: getOptionValue(item)}))}
      />
    </div>
  );
};

export default SelectComponent;
